import { useState, useEffect, useCallback, useRef } from "react";
import { ArrowLeft, Loader2, Upload, RefreshCw, Clock, MessageSquareText, Film, Hourglass, CalendarClock, Gift, Send, Ruler } from "lucide-react";
import { planInfo } from "@/lib/plan";
import PhotoAnnotator from "@/components/lab/PhotoAnnotator";
import PortalMeasures from "@/components/lab/PortalMeasures";
import PortalAppointments from "@/components/lab/PortalAppointments";
import PrivacyForm from "@/components/lab/PrivacyForm";
import CallCountdown, { nextCall, CallReminder } from "@/components/lab/CallCountdown";
import * as api from "@/lib/api";

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const r0 = (n) => Math.round(Number(n) || 0);
const lineProt = (f) => (Number(f.protAnimali) || 0) + (Number(f.protVegetali) || 0);

function DietView({ diets }) {
  const [i, setI] = useState(0);
  const withFoods = diets.filter((d) => (d.meals || []).some((m) => (m.foods || []).length));
  if (!withFoods.length) return <Empty text="Il tuo piano alimentare non è ancora disponibile." />;
  const d = withFoods[Math.min(i, withFoods.length - 1)];
  const all = (d.meals || []).flatMap((m) => m.foods || []);
  const tot = all.reduce((a, f) => ({ kcal: a.kcal + (Number(f.kcal) || 0), c: a.c + (Number(f.carbo) || 0), p: a.p + lineProt(f), g: a.g + (Number(f.grassi) || 0) }), { kcal: 0, c: 0, p: 0, g: 0 });
  return (
    <div className="space-y-4" data-testid="portal-diet">
      {withFoods.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {withFoods.map((x, k) => (
            <button key={x.id} onClick={() => setI(k)} className={`shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${k === i ? "border-[#d4af37]/70 bg-[#d4af37]/15 text-[#e6c86e]" : "border-[#d4af37]/20 text-muted-foreground"}`} data-testid={`portal-diet-tab-${k}`}>{x.nome || `Dieta ${String.fromCharCode(65 + k)}`}</button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-4 gap-2 text-center">
        {[["kcal", r0(tot.kcal), "hsl(var(--c-kcal))"], ["Carbo", `${r0(tot.c)}g`, "hsl(var(--c-carb))"], ["Prot", `${r0(tot.p)}g`, "hsl(var(--c-prot))"], ["Grassi", `${r0(tot.g)}g`, "hsl(var(--c-fat))"]].map(([l, v, c]) => (
          <div key={l} className="rounded-xl border border-[#d4af37]/20 bg-black/20 py-2.5"><div className="font-mono-num text-base font-bold" style={{ color: c }}>{v}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div></div>
        ))}
      </div>
      {(d.meals || []).filter((m) => (m.foods || []).length).map((m) => (
        <div key={m.id} className="rounded-2xl border border-[#d4af37]/20 bg-[#0f1d3a]/80 p-4" data-testid={`portal-meal-${m.id}`}>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-extrabold tracking-[0.14em] text-[#e6c86e]">{m.nome}</span>
            <span className="font-mono-num text-xs text-muted-foreground">{r0(m.foods.reduce((a, f) => a + (Number(f.kcal) || 0), 0))} kcal</span>
          </div>
          <div className="divide-y divide-white/5">
            {m.foods.map((f) => (
              <div key={f.id} className="flex items-baseline justify-between gap-3 py-2">
                <span className="text-sm">{f.nome}</span>
                <span className="shrink-0 font-mono-num text-sm font-semibold">{f.grammi} {f.unit === "pezzo" ? "pz" : "g"}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function VideoCard({ v }) {
  const c = v.correction;
  return (
    <div className="space-y-3 rounded-2xl border border-[#d4af37]/20 bg-[#0f1d3a]/80 p-4" data-testid={`portal-video-${v.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0"><div className="truncate font-semibold">{v.exercise || "Esercizio"}</div><div className="text-[11px] text-muted-foreground">{fmtDT(v.created_at)}</div></div>
        <span className={`shrink-0 rounded-md px-2 py-0.5 text-[10px] font-bold ${c ? "bg-[#d4af37]/15 text-[#e6c86e]" : "bg-[hsl(var(--c-fat))]/20 text-[hsl(var(--c-fat))]"}`}>{c ? "CORREZIONE PRONTA" : "IN REVISIONE"}</span>
      </div>
      <video src={api.portalFileUrl(v.id, "orig")} controls playsInline preload="metadata" className="w-full rounded-xl bg-black" />
      {!c && (
        <div className="flex items-center gap-3 rounded-xl border border-dashed border-[#d4af37]/40 bg-[#d4af37]/5 px-4 py-5" data-testid={`portal-review-${v.id}`}>
          <Hourglass className="h-5 w-5 shrink-0 animate-pulse text-[#e6c86e]" />
          <div><div className="text-sm font-semibold text-[#f3e2a9]">Video in fase di revisione</div><div className="text-[11px] text-slate-400">Qui comparirà la correzione del coach con video e fotogrammi analizzati.</div></div>
        </div>
      )}
      {c && (
        <div className="space-y-3 rounded-xl border border-[#d4af37]/30 bg-[#d4af37]/5 p-3" data-testid={`portal-correction-${v.id}`}>
          <div className="flex items-center gap-1.5 text-xs font-bold text-[#e6c86e]"><MessageSquareText className="h-4 w-4" /> Correzione del coach</div>
          {c.text && <p className="whitespace-pre-wrap text-sm leading-relaxed">{c.text}</p>}
          {(c.frames || []).map((f, k) => (
            <div key={f.id}>
              <div className="relative overflow-hidden rounded-lg bg-black">
                <img src={api.portalFrameUrl(v.id, f.id)} alt="" className="block w-full" />
                <PhotoAnnotator value={f.shapes} readOnly testid={`portal-frame-${v.id}-${k}`} />
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground">Fotogramma {Math.floor(f.time / 60)}:{String(Math.floor(f.time % 60)).padStart(2, "0")}{f.caption ? ` · ${f.caption}` : ""}</div>
            </div>
          ))}
          {c.has_video && (
            <div>
              <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold text-muted-foreground"><Film className="h-3.5 w-3.5" /> Video del coach</div>
              <video src={api.portalFileUrl(v.id, "corr")} controls playsInline preload="metadata" className="w-full rounded-xl bg-black" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function VideoSection({ videos, exercises, onUploaded }) {
  const fileRef = useRef(null);
  const [exercise, setExercise] = useState("");
  const [note, setNote] = useState("");
  const [up, setUp] = useState(0);
  const [err, setErr] = useState("");
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setErr("");
    const f = file.type ? file : new File([file], file.name, { type: /\.mov$/i.test(file.name) ? "video/quicktime" : "video/mp4" });
    setUp(1);
    try { await api.uploadPortalVideo(f, { exercise, note }, (p) => setUp(Math.max(1, p))); setNote(""); onUploaded(); }
    catch (ex) { setErr(ex?.response?.data?.detail || "Caricamento non riuscito, riprova."); }
    finally { setUp(0); }
  };
  return (
    <div className="space-y-4" data-testid="portal-videos">
      <div className="space-y-3 rounded-2xl border border-[#d4af37]/20 bg-[#0f1d3a]/80 p-4">
        <div className="text-sm font-semibold">Carica un video per l'analisi</div>
        <select value={exercise} onChange={(e) => setExercise(e.target.value)} className="lab-input" data-testid="portal-video-exercise">
          <option value="">Seleziona l'esercizio…</option>
          {exercises.map((x) => <option key={x} value={x}>{x}</option>)}
          <option value="Altro">Altro</option>
        </select>
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota per il coach (opzionale)" className="lab-input resize-none" data-testid="portal-video-note" />
        <button onClick={() => (exercise ? fileRef.current?.click() : setErr("Seleziona prima l'esercizio."))} disabled={!!up} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#d4af37] to-[#f1d78a] px-4 py-3 text-sm font-bold text-[#e6c86e]-foreground disabled:opacity-60" data-testid="portal-video-upload-btn">
          {up ? <><Loader2 className="h-4 w-4 animate-spin" /> Caricamento {up}%</> : <><Upload className="h-4 w-4" /> Scegli o registra video</>}
        </button>
        <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={onFile} data-testid="portal-video-input" />
        {up > 0 && <div className="h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full bg-[#d4af37] transition-all" style={{ width: `${up}%` }} /></div>}
        {err && <div className="text-xs text-[hsl(0_72%_68%)]" data-testid="portal-video-error">{err}</div>}
      </div>
      {videos.length ? videos.map((v) => <VideoCard key={v.id} v={v} />) : <Empty text="Non hai ancora caricato video." />}
    </div>
  );
}

const Empty = ({ text }) => <div className="rounded-2xl border border-dashed border-[#d4af37]/25 py-12 text-center text-sm text-slate-400" data-testid="portal-empty">{text}</div>;

const IMG = "https://static.prod-images.emergentagent.com/jobs/3fe4d5b9-6996-4f3e-aa27-7be9eca9be10/images/";
const CARDS = [
  ["dieta", "NUTRIZIONE", "Il tuo piano alimentare", `${IMG}6be1cb5c764658d922cde66ab5a0bfb464b5939dd0dfed607c6d5d7b2b93c78e.jpeg`],
  ["allenamento", "ALLENAMENTO", "Schede e progressioni", `${IMG}0920e4c4af0bdfca33b47f59103f66cb7f48ecc45311d8a908db80144981b5e1.jpeg`],
  ["video", "ANALISI VIDEO", "Carica i video, ricevi le correzioni", `${IMG}2895a70d60c962847aa3cd165e0b6463f30e5dd62b92de21ee955bdd0dc9993d.jpeg`],
  ["misurazioni", "MISURAZIONI", "Invia le tue misure al coach", "https://static.prod-images.emergentagent.com/jobs/3fe4d5b9-6996-4f3e-aa27-7be9eca9be10/images/ae9e8229b168a4e0acd5b390b16b33f587bdac10302cf08320a8c56c3dd34ad3.jpeg"],
  ["appuntamenti", "APPUNTAMENTI", "Prenota una videochiamata con il coach", `${IMG}73fe0cbfc19cdaf32597349099e8764d6ab49e5703a53dd23981aa93e52577ae.jpeg`],
  ["offerte", "OFFERTE", "Promozioni riservate a te", `${IMG}cfbd63df6fb893d9997b64f09efdc3c219a343e1bb63a858e47e40972626ca84.jpeg`],
];

const pad = (n) => String(n).padStart(2, "0");
const useNow = (offset) => {
  const [now, setNow] = useState(Date.now() + offset);
  useEffect(() => { const t = setInterval(() => setNow(Date.now() + offset), 1000); return () => clearInterval(t); }, [offset]);
  return now;
};
const leftParts = (iso, now) => {
  const s = Math.max(0, Math.floor((new Date(iso).getTime() - now) / 1000));
  return { s, d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), sec: s % 60 };
};
const shortLeft = (iso, now) => { const l = leftParts(iso, now); return l.d ? `${l.d}g ${pad(l.h)}:${pad(l.m)}:${pad(l.sec)}` : `${pad(l.h)}:${pad(l.m)}:${pad(l.sec)}`; };

function Card3D({ id, title, sub, img, onClick, index, highlight, badge }) {
  const ref = useRef(null);
  const [t, setT] = useState({ x: 0, y: 0 });
  const move = (e) => {
    const r = ref.current.getBoundingClientRect();
    setT({ x: ((e.clientY - r.top) / r.height - 0.5) * -10, y: ((e.clientX - r.left) / r.width - 0.5) * 12 });
  };
  return (
    <div style={{ perspective: 1000, animation: `fadeUp .6s cubic-bezier(.2,.8,.2,1) ${index * 90}ms both` }}>
      <button ref={ref} onClick={onClick} onPointerMove={move} onPointerLeave={() => setT({ x: 0, y: 0 })}
        className={`relative block h-40 w-full overflow-hidden rounded-3xl text-left transition-transform duration-200 ease-out ${highlight ? "ring-2 ring-[#d4af37] gold-pulse" : "ring-1 ring-[#d4af37]/25"}`}
        style={{ transform: `rotateX(${t.x}deg) rotateY(${t.y}deg)`, transformStyle: "preserve-3d", boxShadow: highlight ? "0 24px 60px -12px rgba(212,175,55,.45), 0 10px 24px rgba(0,0,0,.55)" : "0 24px 50px -14px rgba(0,0,0,.85), 0 8px 18px rgba(3,10,30,.6)" }}
        data-testid={`portal-card-${id}`} data-highlight={highlight ? "true" : "false"}>
        <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ transform: "translateZ(-20px) scale(1.12)" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#07122a] via-[#07122a]/85 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#07122a]/80 to-transparent" />
        {badge && <span className="absolute right-3 top-3 rounded-lg border border-[#d4af37]/60 bg-[#07122a]/85 px-2.5 py-1 font-mono-num text-xs font-bold text-[#f1d78a] backdrop-blur" style={{ transform: "translateZ(40px)" }} data-testid={`portal-card-${id}-badge`}>{badge}</span>}
        <div className="relative flex h-full flex-col justify-end p-5" style={{ transform: "translateZ(30px)" }}>
          <span className="mb-1.5 h-0.5 w-8 rounded bg-gradient-to-r from-[#d4af37] to-transparent" />
          <span className="font-display text-2xl font-extrabold tracking-[0.08em] text-white drop-shadow-lg">{title}</span>
          <span className="text-xs text-[#e6d7a8]/80">{sub}</span>
        </div>
      </button>
    </div>
  );
}

function OfferDetail({ o, now, onOpen }) {
  const opened = !!o.opened_at;
  const l = opened ? leftParts(o.expires_at, now) : null;
  const expired = opened && l.s <= 0;
  return (
    <div className="overflow-hidden rounded-3xl border border-[#d4af37]/40 bg-[#0f1d3a]/90 shadow-[0_24px_60px_-20px_rgba(212,175,55,.35)]" data-testid={`portal-offer-${o.id}`}>
      {o.image && <img src={o.image} alt="" className="block max-h-[60vh] w-full bg-black object-contain" />}
      <div className="space-y-3 p-5">
        <h2 className="font-display text-2xl font-extrabold leading-tight">{o.title}</h2>
        {o.subtitle && <p className="text-sm text-slate-300">{o.subtitle}</p>}
        {(o.price_original != null || o.price_promo != null) && (
          <div className="flex items-baseline gap-3">
            {o.price_original != null && <span className="text-lg text-slate-400 line-through">€ {o.price_original}</span>}
            {o.price_promo != null && <span className="font-display text-4xl font-extrabold text-[#e6c86e]">€ {o.price_promo}</span>}
          </div>
        )}
        {o.description && <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-200">{o.description}</p>}
        {!opened ? (
          <button onClick={() => onOpen(o)} className="w-full rounded-2xl bg-gradient-to-r from-[#d4af37] to-[#f1d78a] py-4 text-sm font-extrabold tracking-wide text-[#0a1428]" data-testid={`portal-offer-open-${o.id}`}>
            <Gift className="mr-1.5 inline h-4 w-4" /> SCOPRI L'OFFERTA E AVVIA IL TIMER
          </button>
        ) : (
          <>
            <div className="rounded-2xl border border-[#d4af37]/30 bg-[#07122a] p-4 text-center" data-testid={`portal-offer-countdown-${o.id}`}>
              {expired ? <div className="font-display text-2xl font-bold text-red-400">OFFERTA SCADUTA</div> : (
                <>
                  <div className="text-[11px] font-bold tracking-[0.2em] text-[#e6c86e]">L'OFFERTA SCADE TRA</div>
                  <div className="mt-2 grid grid-cols-4 gap-2">
                    {[[l.d, "GIORNI"], [l.h, "ORE"], [l.m, "MINUTI"], [l.sec, "SECONDI"]].map(([v, k]) => (
                      <div key={k}><div className="rounded-xl border border-[#d4af37]/25 bg-[#0f1d3a] py-2 font-mono-num text-2xl font-extrabold">{pad(v)}</div><div className="mt-1 text-[9px] font-bold tracking-wider text-slate-400">{k}</div></div>
                    ))}
                  </div>
                </>
              )}
            </div>
            {o.telegram_url && !expired ? (
              <a href={o.telegram_url} target="_blank" rel="noopener noreferrer" className="block w-full rounded-2xl bg-gradient-to-r from-[#d4af37] to-[#f1d78a] py-4 text-center text-sm font-extrabold tracking-wide text-[#0a1428]" data-testid={`portal-offer-cta-${o.id}`}>
                <Send className="mr-1.5 inline h-4 w-4" /> VOGLIO APPROFITTARE DELL'OFFERTA
              </a>
            ) : <div className="w-full rounded-2xl bg-white/5 py-4 text-center text-sm font-bold text-slate-500" data-testid={`portal-offer-cta-${o.id}`}>{expired ? "OFFERTA SCADUTA" : "NON DISPONIBILE"}</div>}
          </>
        )}
      </div>
    </div>
  );
}

export default function PatientPortal() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [section, setSection] = useState(null);
  const [offset, setOffset] = useState(0);
  const now = useNow(offset);
  const load = useCallback(async () => {
    try {
      const d = await api.portalMe();
      setData(d); setState("ok");
      if (d.server_now) setOffset(new Date(d.server_now).getTime() - Date.now());
    } catch (e) { setState(e?.response?.status === 401 ? "invalid" : "error"); }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    const vis = () => document.visibilityState === "visible" && load();
    document.addEventListener("visibilitychange", vis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", vis); };
  }, [load]);
  useEffect(() => { document.title = "AGFIT · La tua area personale"; }, []);

  const openOffer = async (o) => {
    try { const r = await api.portalOpenOffer(o.id); setData((d) => ({ ...d, offers: r.offers })); } catch (e) { load(); }
  };

  if (state === "loading") return <div className="grid min-h-screen place-items-center bg-[#07122a] text-[#d4af37]"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (state !== "ok") {
    return (
      <div className="grid min-h-screen place-items-center bg-[#07122a] px-6 text-center text-white" data-testid="portal-invalid">
        <div><h1 className="font-display text-2xl font-bold text-[#e6c86e]">Link non valido</h1><p className="mt-2 text-sm text-slate-400">{state === "invalid" ? "Il link non è più attivo. Chiedi al tuo coach un nuovo link." : "Impossibile caricare i dati, riprova tra poco."}</p></div>
      </div>
    );
  }

  if (data.privacy_required) {
    return (
      <div className="min-h-screen bg-[#07122a] text-white">
        <div className="mx-auto max-w-xl px-4 pb-16 pt-7" data-testid="patient-portal-privacy">
          <img src="/agfit-logo.png" alt="AGFIT" className="mb-3 h-16 w-16 rounded-2xl object-cover shadow-[0_10px_30px_-10px_rgba(212,175,55,.5)] ring-1 ring-[#d4af37]/40" data-testid="portal-logo" />
          <h1 className="mb-5 font-display text-3xl font-extrabold">Ciao {data.nome || ""}</h1>
          <PrivacyForm prefill={data.privacy_prefill} onDone={load} />
        </div>
      </div>
    );
  }
  const offers = data.offers || [];
  const activeOffers = offers.filter((o) => o.status === "nuova" || (o.status === "attiva" && new Date(o.expires_at).getTime() > now));
  const running = activeOffers.filter((o) => o.opened_at).sort((a, b) => new Date(a.expires_at) - new Date(b.expires_at))[0];
  const offerBadge = running ? shortLeft(running.expires_at, now) : (activeOffers.length ? "NUOVA" : null);
  const plan = planInfo({ plan: data.plan });
  const title = section ? CARDS.find((c) => c[0] === section)[1] : `Ciao ${data.nome || ""}`;

  return (
    <div className="min-h-screen bg-[#07122a] text-white" style={{ backgroundImage: "radial-gradient(1200px 600px at 100% -10%, rgba(212,175,55,.12), transparent 60%), radial-gradient(900px 500px at -10% 110%, rgba(30,64,140,.35), transparent 60%)" }}>
      <div className="mx-auto max-w-xl px-4 pb-16 pt-7" data-testid="patient-portal">
        <header className="mb-6">
          {section ? (
            <button onClick={() => setSection(null)} className="mb-3 inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#e6c86e]" data-testid="portal-back"><ArrowLeft className="h-4 w-4" /> Indietro</button>
          ) : <img src="/agfit-logo.png" alt="AGFIT" className="mb-3 h-16 w-16 rounded-2xl object-cover shadow-[0_10px_30px_-10px_rgba(212,175,55,.5)] ring-1 ring-[#d4af37]/40" data-testid="portal-logo" />}
          <h1 className="font-display text-3xl font-extrabold leading-tight" data-testid="portal-title">{title}</h1>
          {!section && plan.hasPlan && (
            <div className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${plan.expired ? "border-red-400/40 bg-red-400/10 text-red-300" : "border-[#d4af37]/40 bg-[#d4af37]/10 text-[#f1d78a]"}`} data-testid="portal-plan-days">
              <CalendarClock className="h-3.5 w-3.5" />
              {plan.expired ? `Percorso scaduto da ${Math.abs(plan.daysLeft)} giorni` : `${plan.daysLeft} giorni alla scadenza · ${plan.end.toLocaleDateString("it-IT")}`}
            </div>
          )}
          <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-400" data-testid="portal-updated">
            <Clock className="h-3.5 w-3.5" /> Ultimo aggiornamento: {fmtDT(data.updated_at)}
            <button onClick={load} title="Aggiorna" className="rounded p-0.5 hover:text-[#e6c86e]" data-testid="portal-refresh"><RefreshCw className="h-3.5 w-3.5" /></button>
          </div>
        </header>

        {!section && <CallReminder appt={nextCall(data.appointments)} />}
        {!section && <CallCountdown appt={nextCall(data.appointments)} />}
        {!section && data.measures_due && (
          <button onClick={() => setSection("misurazioni")} className="mb-4 flex w-full items-center gap-3 rounded-2xl border border-[#d4af37]/60 bg-[#d4af37]/10 p-4 text-left gold-pulse" data-testid="portal-measures-reminder">
            <Ruler className="h-6 w-6 shrink-0 text-[#d4af37]" />
            <span><span className="block text-sm font-bold text-[#f1d78a]">È ora delle misurazioni settimanali</span><span className="text-xs text-slate-300">Invia peso e circonferenze al tuo coach: bastano 2 minuti.</span></span>
          </button>
        )}
        {!section && (
          <div className="space-y-4">
            {CARDS.map(([id, t, sub, img], k) => (
              <Card3D key={id} id={id} title={t} sub={sub} img={img} index={k} onClick={() => setSection(id)}
                highlight={(id === "offerte" && activeOffers.length > 0) || (id === "misurazioni" && data.measures_due)} badge={id === "offerte" ? offerBadge : id === "misurazioni" && data.measures_due ? "DA INVIARE" : null} />
            ))}
          </div>
        )}
        {section === "dieta" && <DietView diets={data.diets || []} />}
        {section === "allenamento" && (data.workout_id ? (
          <iframe key={data.updated_at} title="Allenamento" src={`/api/workout/${data.workout_id}?view=cards`} className="h-[80vh] w-full rounded-2xl border border-[#d4af37]/20 bg-[#0b0f14]" data-testid="portal-workout-frame" />
        ) : <Empty text="Il tuo programma di allenamento non è ancora disponibile." />)}
        {section === "video" && <VideoSection videos={data.videos || []} exercises={data.exercises || []} onUploaded={load} />}
        {section === "misurazioni" && <PortalMeasures last={data.measures_last} history={data.measures_history || []} onSent={load} />}
        {section === "appuntamenti" && <PortalAppointments items={data.appointments || []} booking={data.booking} patientId={data.booking?.pid} onBooked={load} />}
        {section === "offerte" && (
          <div className="space-y-4" data-testid="portal-offers">
            {offers.length ? offers.map((o) => <OfferDetail key={o.id} o={o} now={now} onOpen={openOffer} />) : <Empty text="Al momento non ci sono offerte per te." />}
          </div>
        )}
        <style>{"@keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}@keyframes goldPulse{0%,100%{outline-color:rgba(212,175,55,0)}50%{outline-color:rgba(212,175,55,.35)}}.gold-pulse{outline:6px solid rgba(212,175,55,0);outline-offset:2px;animation:goldPulse 2.4s ease-in-out infinite}"}</style>
      </div>
    </div>
  );
}
